import TimesRegUrl from "../assets/fonts/TimesNewRoman.ttf";
import TimesBoldUrl from "../assets/fonts/TimesNewRoman-Bold.ttf";
import TimesItUrl from "../assets/fonts/TimesNewRoman-Italic.ttf";
import TimesBoldItUrl from "../assets/fonts/TimesNewRoman-BoldItalic.ttf";

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import { useEffect } from "react";

import { useParams } from "react-router-dom";

import { fetchPreventivePdf } from "../slices/preventivePdfSlice";

import pdfMake from "pdfmake/build/pdfmake.js";

let TIMES_READY = false;
let TIMES_VFS = null;
let TIMES_FONTS = null;

const fetchAsBase64Raw = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => {
        const r = new FileReader();
        r.onloadend = () => resolve(String(r.result).split(",")[1]); // чистый base64
        r.readAsDataURL(blob);
    });
};

const ensureTimes = async () => {
    if (TIMES_READY && TIMES_VFS && TIMES_FONTS) {
        return { fonts: TIMES_FONTS, vfs: TIMES_VFS };
    }

    const [reg, bold, it, bi] = await Promise.all([
        fetchAsBase64Raw(TimesRegUrl),
        fetchAsBase64Raw(TimesBoldUrl),
        fetchAsBase64Raw(TimesItUrl),
        fetchAsBase64Raw(TimesBoldItUrl),
    ]);

    const vfs = {
        "TimesNewRoman.ttf": reg,
        "TimesNewRoman-Bold.ttf": bold,
        "TimesNewRoman-Italic.ttf": it,
        "TimesNewRoman-BoldItalic.ttf": bi,
    };

    const fonts = {
        Times: {
            normal: "TimesNewRoman.ttf",
            bold: "TimesNewRoman-Bold.ttf",
            italics: "TimesNewRoman-Italic.ttf",
            bolditalics: "TimesNewRoman-BoldItalic.ttf",
        },
    };

    // продублируем в глобальные поля pdfMake (на всякий случай)
    pdfMake.vfs = { ...(pdfMake.vfs || {}), ...vfs };
    pdfMake.fonts = { ...(pdfMake.fonts || {}), ...fonts };

    TIMES_READY = true;
    TIMES_VFS = vfs;
    TIMES_FONTS = fonts;

    return { fonts, vfs };
};

export const usePreventivePdf = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const token = useSelector((state) => state.auth.token);
    const columns = useSelector((state) => state.preventivePdf.columns);
    const table_columns = useSelector((state) => state.preventivePdf.table_columns);
    const data = useSelector((state) => state.preventivePdf.data);
    const relationships = useSelector((state) => state.preventivePdf.relationships);

    useEffect(() => {
        if (token && id) {
            dispatch(fetchPreventivePdf({ token, id }));
        }
    }, [dispatch, id, token]);

    const fetchImageBase64 = async (imagePath) => {
        try {
            const response = await fetch(imagePath);
            const blob = await response.blob();
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
        } catch {
            return null;
        }
    };

    const renderField = (value) => {
        if (value === null || value === undefined) return "Отсутствует";

        if (Array.isArray(value)) {
            if (value.length > 0 && typeof value[0] === "object") {
                const names = value
                    .map((p) => (typeof p === "string" ? p : p?.name))
                    .filter(Boolean);
                return names.length ? names.join(", ") : "Отсутствует";
            }
            return value.join(", ");
        }

        if (typeof value === "object") {
            if (value instanceof Date) return value.toLocaleDateString("ru-RU");
            const maybeName = value?.name ?? value?.title ?? null;
            return maybeName ?? "Отсутствует";
        }

        return String(value);
    };

    const generatePdf = async () => {
        const { fonts, vfs } = await ensureTimes();
        const attrs = data?.attributes ?? {};

        // брэнч-картинка
        const branchId = attrs.branch_office_id;
        let branchImagePath = "/img/pvass.png";
        switch (branchId) {
            case 1: branchImagePath = "/img/pvass_branch_1.png"; break;
            case 2: branchImagePath = "/img/pvass_branch_2.png"; break;
            case 3: branchImagePath = "/img/pvass_branch_3.png"; break;
            case 4: branchImagePath = "/img/pvass_branch_4.png"; break;
            case 5: branchImagePath = "/img/pvass_branch_5.png"; break;
            case 6: branchImagePath = "/img/pvass_branch_6.png"; break;
            case 7: branchImagePath = "/img/pvass_branch_7.png"; break;
            case 8: branchImagePath = "/img/pvass_branch_8.png"; break;
            case 9: branchImagePath = "/img/pvass_branch_9.png"; break;
            case 10: branchImagePath = "/img/pvass_branch_10.png"; break;
            case 11: branchImagePath = "/img/pvass_branch_11.png"; break;
            case 12: branchImagePath = "/img/pvass_branch_12.png"; break;
            case 13: branchImagePath = "/img/pvass_branch_13.png"; break;
            default: branchImagePath = "/img/pvass.png"; break;
        }
        const img = await fetchImageBase64(branchImagePath);

        const content = [];

        if (img) {
            content.push({
                image: img,
                width: 600,
                alignment: "center",
                margin: [0, 0, 0, 10],
            });
        }

        content.push({
            text:
                "ҚАУІПТІ ӨНДІРІСТІК ОБЪЕКТІНІ \n" +
                "ПРОФИЛАКТИКАЛЫҚ ТЕКСЕРУ АКТІСІ\n" +
                "АКТ ПРОФИЛАКТИЧЕСКОГО ОБСЛЕДОВАНИЯ \n" +
                "ОПАСНОГО ПРОИЗВОДСТВЕННОГО ОБЪЕКТА\n",
            style: "header",
            alignment: "center",
        });

        const surveyDateStr = attrs.survey_date
            ? new Date(attrs.survey_date).toLocaleDateString("ru-RU")
            : "Дата не указана";

        content.push({
            text: `${surveyDateStr} жыл`,
            alignment: "right",
            italics: true,
            margin: [0, 0, 0, 10],
        });

        // поля из API.columns
        columns?.forEach((col) => {
            const value = renderField(attrs[col.key]);
            content.push({
                columns: [
                    { text: col.name, bold: true, fontSize: 10, width: "40%", margin: [0, 4, 0, 0] },
                    {
                        stack: [
                            { text: value, fontSize: 10, margin: [0, 0, 0, 1] },
                            { canvas: [{ type: "line", x1: 0, y1: 0, x2: 300, y2: 0, lineWidth: 0.5 }], margin: [0, 0, 0, 3] },
                        ],
                        width: "*",
                    },
                ],
                columnGap: 10,
                margin: [0, 2, 0, 2],
            });
        });

        content.push({
            text:
                "Тексеру нәтижесі бойынша келесі бұзушылықтар анықталды:\n" +
                "В результате обследования выявлены следующие нарушения:",
            margin: [0, 10, 0, 5],
        });

        // таблица по table_columns + data.relationships
        const rows =
            Array.isArray(relationships)
                ? relationships.map((rel, idx) => {
                    const item = rel?.attributes ?? {}; // ВАЖНО: это объект, не массив
                    return [
                        { text: String(idx + 1), alignment: "center" },
                        ...table_columns.map((col) => {
                            const val = item[col.key];
                            if (val == null || val === "") return { text: " ", alignment: "center" };

                            if (col.key.includes("date")) {
                                const d = new Date(val);
                                const safe = isNaN(+d) ? String(val) : d.toLocaleDateString("ru-RU");
                                return { text: safe, alignment: "center" };
                            }
                            return { text: renderField(val), alignment: "left" };
                        }),
                    ];
                })
                : [];

        content.push({
            table: {
                headerRows: 1,
                widths: ["4%", ...table_columns.map(() => "*")],
                body: [
                    [
                        { text: "№", bold: true, alignment: "center" },
                        ...table_columns.map((c) => ({ text: c.name, bold: true, alignment: "center" })),
                    ],
                    ...rows,
                ],
            },
            layout: {
                hLineWidth: () => 0.1,
                vLineWidth: () => 0.1,
                hLineColor: () => "#000",
                vLineColor: () => "#000",
            },
            margin: [0, 10, 0, 20],
        });

        // подписи
        const underlined = (text = " ", { align = "left", margin = [0, 0, 0, 6] } = {}) => ({
            table: { widths: ["*"], body: [[{ text, alignment: align, margin: [0, 0, 0, 2] }]] },
            layout: {
                hLineWidth: (i, node) => (i === node.table.body.length ? 0.5 : 0),
                hLineColor: () => "#000",
                vLineWidth: () => 0,
                paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
            },
            margin,
        });

        const buildSignatureSection = (title, people = []) => {
            const safe = (v) => (v ? String(v) : " ");
            return [
                [{ text: title, colSpan: 3, border: [false, false, false, false], margin: [0, 10, 0, 6] }, {}, {}],
                ...people.map((p) => ([
                    { stack: [underlined(safe(p?.position)), { text: "(Лауазымы/Должность)", italics: true, margin: [0, 4, 0, 10] }] },
                    { stack: [underlined(" "), { text: "(Қолы/подпись)", italics: true, margin: [0, 4, 0, 10] }] },
                    { stack: [underlined(safe(p?.name)), { text: "(Т.А.Ә., ФИО)", italics: true, margin: [0, 4, 0, 10] }] },
                ])),
            ];
        };

        content.push({ text: "Актіге қол қойғандар/Акт подписали:", margin: [0, 10, 0, 5] });

        const listOf = (v) => Array.isArray(v) ? v : (v ? [v] : []);
        const senders = listOf(attrs.senders);
        const spokesmen = listOf(attrs.spokesmen);
        const reviewers = listOf(attrs.reviewer);
        const hasReviewer = reviewers.some(r => (r?.name?.trim?.() || r?.position?.trim?.()));

        const signatureBody = [
            ...buildSignatureSection("ҚӘАҚК РӨШ өкілі/Представитель РЦШ ПВАСС:", senders),
            ...buildSignatureSection("Объектінің өкілі/Представитель объекта:", spokesmen),
            ...(hasReviewer ? buildSignatureSection("Актімен таныстырылды/С актом ознакомлен:", reviewers) : []),
        ];

        content.push({
            table: { widths: ["33%", "33%", "34%"], body: signatureBody },
            layout: "noBorders",
            margin: [0, 10, 0, 0],
        });

        const docDefinition = {
            pageSize: "A4",
            pageMargins: [20, 20, 20, 20],
            defaultStyle: { font: "Times", fontSize: 10 },
            styles: { header: { fontSize: 9, bold: true, margin: [0, 0, 0, 10] } },
            content,
        };

        try {
            pdfMake.createPdf(docDefinition, undefined, fonts, vfs)
                .download(`Акт проф.обследования.pdf`);
        } catch (err) {
            console.error("Ошибка генерации PDF:", err);
        }
    };

    return {
        generatePdf,
    };
};